import React, { useEffect, useState } from "react";
import { KeyValue } from "@/main/models/common";
import { useForm } from "react-hook-form";
import { getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { getPartType, usePartTypeReducer } from "@/main/store/partType";
import { useDispatch } from "react-redux";
import { getManufacturers, useManufacturerReducer } from "@/main/store/vehicle/manufacturer";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getClaimType, useClaimTypeReducer } from "@/main/store/claimType";
import KogAutoCompleteVehicleControlled from "@/main/components/shared/inputAutoComplete/autoCompleteVehicleControlled";
import { toast } from "react-toastify";
import { DirectAssignCriteriaRequest } from "@/main/models/request/directAssignCriteriaRequest";
import { directAssignCriteriaApi } from "@/main/api";
import { DirectAssignCriteriaResponse } from "@/main/models/response/directAssignCriteriaResponse";
import SecondaryButton from "../button/secondaryButton";

import * as S from "./styles";
import KOGAutoCompleteCheckbox, { IOption } from "../inputAutoCompleteCheck";
import KogInputText from "../inputText/inputText";
import KogModalSideComponent from "../../modal/ModalSide";
import useAsyncModalConfirmReason from "./../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

interface IDirectAssignCriteriaModal {
	guidInsurer?: string | null;
	guidBodyShop?: string | null;
	dataDirectAssignCriteriaGrid: DirectAssignCriteriaResponse[];
	setDirectAssignCriteriaGrid: React.Dispatch<React.SetStateAction<DirectAssignCriteriaResponse[]>>;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
function DirectAssignCriteriaModalContent({
	guidInsurer = null,
	guidBodyShop = null,
	setDirectAssignCriteriaGrid,
	setOpen,
}: IDirectAssignCriteriaModal) {
	const [ageStart, setAgeStart] = useState<string>("");
	const [ageEnd, setAgeEnd] = useState<string>("");
	const [optionsSelectedClaimType, setOptionsSelectedClaimType] = useState<IOption[]>([]);
	const [optionsSelectedPartType, setOptionsSelectedPartType] = useState<IOption[]>([]);
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [errorAgeStart, setErrorAgeStart] = useState<string | null>(null);
	const [errorAgeEnd, setErrorAgeEnd] = useState<string | null>(null);
	const [errorClaimType, setErrorClaimType] = useState<string | null>(null);
	const [errorPartType, setErrorPartType] = useState<string | null>(null);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	const dispatch = useDispatch();

	const partTypeReducer = usePartTypeReducer();
	const manufacturerReducer = useManufacturerReducer();
	useCountryReducer();
	const claimTypeReducer = useClaimTypeReducer();
	const isDisabledManufacture = () => manufacturerReducer.manufacturers.data === null;

	const INITIAL_FORM_STATE_SAVE = {
		make: null as KeyValue | null,
		ageStart: "",
		ageEnd: "",
		claimType: null as KeyValue | null,
		partType: null as KeyValue | null,
	};

	type IDirectAssignCriteriaFormSave = typeof INITIAL_FORM_STATE_SAVE;
	const { control, setValue, reset, handleSubmit } = useForm<IDirectAssignCriteriaFormSave>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_SAVE,
	});

	function validateField(
		value: IOption[] | string,
		setError: React.Dispatch<React.SetStateAction<string | null>>,
		label: string,
	) {
		if (value.length === 0 || value === "") {
			setError(label);
			return false;
		}
		setError(null);
		return true;
	}

	function validateForm() {
		const isValidAgeStart = validateField(
			ageStart,
			setErrorAgeStart,
			getTranslateByKey("purchaseCriteria.yearStartNotInformed", true),
		);
		const isValidAgeEnd = validateField(
			ageEnd,
			setErrorAgeEnd,
			getTranslateByKey("purchaseCriteria.yearEndNotInformed", true),
		);
		const isValidClaimType = validateField(
			optionsSelectedClaimType,
			setErrorClaimType,
			getTranslateByKey("purchaseCriteria.claimTypeNotInformed", true),
		);
		const isValidPartType = validateField(
			optionsSelectedPartType,
			setErrorPartType,
			getTranslateByKey("purchaseCriteria.partTypeNotInformed", true),
		);

		return isValidAgeStart && isValidAgeEnd && isValidClaimType && isValidPartType;
	}

	async function onSubmitSave(data: IDirectAssignCriteriaFormSave) {
		try {
			if (!validateForm()) return;

			if (Number(ageEnd) === 0) {
				setErrorAgeEnd(getTranslateByKey("purchaseCriteria.theAgeEndCannotBeZero", true));
				return false;
			}

			if (Number(ageStart) >= Number(ageEnd)) {
				setErrorAgeStart(getTranslateByKey("purchaseCriteria.endYearCannotBeLessThanStartYear", true));
				setErrorAgeEnd(getTranslateByKey("purchaseCriteria.endYearCannotBeLessThanStartYear", true));
				return false;
			}

			let resultConfirm = null;
			let filesRequest = undefined;

			if (guidInsurer || guidBodyShop) {
				resultConfirm = await getConfirmationReason(
					getTranslateByKey("information.confirmChangeTitle"),
					ReasonTypeEnum.ChangeCompany,
				);

				if (!resultConfirm.confirmed) {
					setLoadingSave(false);
					return;
				}

				if (resultConfirm.files && resultConfirm.files.length > 0) {
					filesRequest = await Promise.all(
						resultConfirm.files.map(async (item) => {
							const base64DataUrl = await toBase64(item);
							return {
								fileName: item.name,
								contentType: item.type,
								fileBase64: base64DataUrl as string,
							};
						}),
					);
				}
			}

			setLoadingSave(true);

			const requestDirectAssignCriteria = removeEmptyValues({
				guidBodyShop,
				guidInsurer,
				guidVehicleManufacturer: data?.make?.key,
				yearStart: Number(ageStart),
				yearEnd: Number(ageEnd),
				guidClaimTypes: optionsSelectedClaimType.map((claimType) => {
					return claimType.key;
				}),
				guidPartTypes: optionsSelectedPartType.map((partType) => {
					return partType.key;
				}),
			}) as DirectAssignCriteriaRequest;

			if (guidInsurer || guidBodyShop) {
				requestDirectAssignCriteria.changeJustification = {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				};
			}

			directAssignCriteriaApi.saveDirectAssignCriteria(requestDirectAssignCriteria).then((res) => {
				const resData = res.data.result;

				setDirectAssignCriteriaGrid((prevState) => {
					return [...prevState, resData];
				});

				toast.success(getTranslateByKey("directAssignCriteria.directAssignCriteriaSavedSuccessfully"));

				setAgeStart("");
				setAgeEnd("");
				reset(INITIAL_FORM_STATE_SAVE);
				setOptionsSelectedClaimType([]);
				setOptionsSelectedPartType([]);
				setOpen(false);
			});
		} catch {
		} finally {
			setLoadingSave(false);
		}
	}

	useEffect(() => {
		dispatch(getPartType());
		dispatch(getCountriesUser());
		dispatch(getClaimType());
		dispatch(getPartType());
		reset(INITIAL_FORM_STATE_SAVE);
	}, []);

	useEffect(() => {
		dispatch(getManufacturers({}));
	}, []);

	function handleChangeAge(value: string, setValueState: React.Dispatch<React.SetStateAction<string>>) {
		const onlyNumbers = value.replace(/\D/g, "");
		setValueState(onlyNumbers);
	}

	return (
		<>
			<S.Title>{getTranslateByKey("information.registerDirectAssignCriteria")}</S.Title>
			<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
				<S.Container>
					<KogAutoCompleteVehicleControlled
						className="ignore-click"
						id="id-directAssignCriteriaRegister-dropdown-manufacturer"
						label={getTranslateByKey("information.make")}
						nameCustom="make"
						control={control}
						setValueCustom={setValue}
						options={manufacturerReducer.manufacturers.data}
						isLoading={manufacturerReducer.manufacturers.isLoading}
						disabled={isDisabledManufacture()}
						rulesCustom={{ required: false }}
						onChangeCustom={() => {}}
					/>
					<KogInputText
						id="id-directAssignCriteriaRegister-number-ageStart"
						name="ageStart"
						label={getTranslateByKey("information.ageStartYear")}
						hasOnlyNumber
						required
						value={ageStart}
						onChangeCustom={(e) => {
							handleChangeAge(e, setAgeStart);
							validateField(e, setErrorAgeStart, getTranslateByKey("purchaseCriteria.yearStartNotInformed", true));
						}}
						errorMessageCustom={errorAgeStart}
						inputProps={{ maxLength: 3 }}
					/>
					<KogInputText
						id="id-directAssignCriteriaRegister-number-ageEnd"
						name="ageEnd"
						label={getTranslateByKey("information.ageEndYear")}
						hasOnlyNumber
						required
						value={ageEnd}
						onChangeCustom={(e) => {
							handleChangeAge(e, setAgeEnd);
							validateField(e, setErrorAgeEnd, getTranslateByKey("purchaseCriteria.yearEndNotInformed", true));
						}}
						errorMessageCustom={errorAgeEnd}
						inputProps={{ maxLength: 3 }}
					/>
					<KOGAutoCompleteCheckbox
						id="id-directAssignCriteriaRegister-multiSelect-claimType"
						classNameCustom="ignore-click"
						label={getTranslateByKey("information.claimType")}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						options={claimTypeReducer.claimType.data || []}
						isLoading={claimTypeReducer.claimType.isLoading}
						valueCustom={optionsSelectedClaimType}
						setValueCustom={setOptionsSelectedClaimType}
						onChangeCustom={(value) => {
							validateField(value, setErrorClaimType, getTranslateByKey("purchaseCriteria.claimTypeNotInformed", true));
						}}
						errorMessageCustom={errorClaimType}
						customInputProps={{
							required: true,
						}}
						required
					/>
					<KOGAutoCompleteCheckbox
						id="id-directAssignCriteriaRegister-multiSelect-partTypes"
						required
						classNameCustom="ignore-click"
						label={getTranslateByKey("information.partTypes")}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						options={partTypeReducer.partType.data || []}
						isLoading={partTypeReducer.partType.isLoading}
						valueCustom={optionsSelectedPartType}
						setValueCustom={setOptionsSelectedPartType}
						onChangeCustom={(value) => {
							validateField(value, setErrorPartType, getTranslateByKey("purchaseCriteria.partTypeNotInformed", true));
						}}
						errorMessageCustom={errorPartType}
						customInputProps={{
							required: true,
						}}
					/>
				</S.Container>
				<S.AreaButton>
					<SecondaryButton id="id-directAssignCriteria-button-save" type="submit" isLoading={loadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</S.AreaButton>
			</form>
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

function DirectAssignCriteriaModal(props: IDirectAssignCriteriaModal) {
	return (
		<KogModalSideComponent open={props.open} closeModal={() => props.setOpen(false)}>
			{props.open && <DirectAssignCriteriaModalContent {...props} />}
		</KogModalSideComponent>
	);
}

export default DirectAssignCriteriaModal;
